/**
 * Settings → Calendar — verbatim port of screens-settings-calendar.jsx.
 * Admins rename / recolour event-type categories and add custom ones,
 * persisted through the CalCategories store so the calendar views recolour
 * on save. Curated palette only (F-08), built-ins locked against deletion,
 * Save / Revert / Reset match the SettingsBranding rhythm.
 */
import { useEffect, useMemo, useState } from "react";
import { alpha, Btn, Card, Icon, IconBtn, Pill, T, tx } from "@v0y4ge/jrni-ui";
import { PageHeader } from "../../shell/chrome";
import { toast } from "../../shell/toast";
import { useUnsavedGuard } from "../../shell/editor-hooks";
import SettingsShell from "./SettingsShell";
import {
  getCalCategories, setCalCategories, resetCalCategories, subscribeCalCategories,
  CAL_CATEGORY_PALETTE, BUILT_IN_CAL_CATEGORIES, type CalCategory, type PaletteColor,
} from "../calendar/cal-categories";
import { useT, t as i18nT } from "../../lib/i18n";

const mono = () => ({ fontFamily: T.fontMono, fontSize: T.text.label.fontSize });

export default function SettingsCalendar() {
  const t = useT();
  const [stored, setStored] = useState<CalCategory[]>(getCalCategories);
  const [rows, setRows] = useState<CalCategory[]>(() => getCalCategories());
  const palette = CAL_CATEGORY_PALETTE;

  /* Pull in changes made from another surface. */
  useEffect(() => subscribeCalCategories(() => { setStored(getCalCategories()); }), []);

  /* Live count per category — how many events map to each typeId. The
   * calendar events live server-side; until the contract exposes them here
   * the counts derive from the built-in match table (0 for custom). */
  const counts = useMemo(() => {
    const out: Record<string, number> = {};
    rows.forEach((r) => { out[r.id] = r.builtIn ? (r.matchColors || []).length : 0; });
    return out;
  }, [rows]);

  const dirty = JSON.stringify(rows.map((r) => ({ id: r.id, name: r.name, color: r.color })))
             !== JSON.stringify(stored.map((r) => ({ id: r.id, name: r.name, color: r.color })));
  useUnsavedGuard(dirty);

  const update = (id: string, patch: Partial<CalCategory>) =>
    setRows((arr) => arr.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  const removeRow = (id: string) => setRows((arr) => arr.filter((r) => r.id !== id));
  const addRow = () => {
    const id = "cust_" + Math.random().toString(36).slice(2, 8);
    const used = new Set(rows.map((r) => r.color.toUpperCase()));
    const next = (palette.find((p) => !used.has(p.hex.toUpperCase())) || palette[0]).hex;
    setRows((arr) => [...arr, { id, name: "New category", color: next, builtIn: false, matchColors: [] }]); // ui-config — new-category default row
  };

  const onSave = async () => {
    const cleaned = rows.map((r) => ({ ...r, name: (r.name || "").trim() || "Untitled category" }));
    try {
      // setCalCategories returns the replaceCalendarCategories promise — await it
      // before declaring success (previously this toasted over an unawaited write).
      await setCalCategories(cleaned);
      setStored(getCalCategories()); setRows(getCalCategories());
      toast({ tone: "success", title: t("settings.calendar.calendarCategoriesSaved", "Calendar categories saved"), subtitle: t("settings.calendar.weekMonthAndDayViewsNowUseYourColourScheme", "Week, month, and day views now use your colour scheme.") });
    } catch (e) {
      console.warn("replaceCalendarCategories failed:", e);
      toast({ tone: "danger", title: t("settings.calendar.couldnTSaveCategories", "Couldn't save categories"), subtitle: t("settings.calendar.yourEditsAreStillHereRetryInAMoment", "Your edits are still here — retry in a moment.") });
    }
  };
  const onRevert = () => setRows(stored.map((c) => ({ ...c })));
  const onResetDefaults = () => {
    const fresh = resetCalCategories();
    setStored(fresh); setRows(fresh.map((c) => ({ ...c })));
    toast({ tone: "info", title: t("settings.calendar.categoriesResetToDefaults", "Categories reset to defaults") });
  };

  const savedJSON = JSON.stringify(stored.map((c) => ({ id: c.id, name: c.name, color: c.color })));
  const defaultsJSON = JSON.stringify(BUILT_IN_CAL_CATEGORIES.map((c) => ({ id: c.id, name: c.name, color: c.color })));
  const atDefaults = savedJSON === defaultsJSON;

  return (
    <SettingsShell activeTab="calendar">
      <PageHeader
        title={t("settings.calendar.calendar", "Calendar")}
        subtitle={t("settings.calendar.nameAndColourTheEventTypeCategoriesThatPaintYourCalendar", "Name and colour the event-type categories that paint your calendar.")}
        actions={<>
          <Btn variant="secondary" size="sm" onClick={onResetDefaults} disabled={atDefaults && !dirty}>{t("settings.calendar.resetToDefaults", "Reset to defaults")}</Btn>
          <Btn variant="secondary" onClick={onRevert} disabled={!dirty}>{t("settings.calendar.revert", "Revert")}</Btn>
          <Btn onClick={onSave} disabled={!dirty}>{dirty ? t("account.details.saveChanges", "Save changes") : t("workforce.coaching.saved", "Saved")}</Btn>
        </>}
      />
      <div style={{ flex: 1, overflow: "auto", padding: `${T.space[4]} ${T.space[6]} ${T.space[6]}`, display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: T.space[4], alignItems: "start" }}>

        {/* Categories list */}
        <Card style={{ padding: 0, display: "flex", flexDirection: "column" }}>
          <div style={{ padding: `${T.space[4]} ${T.space[5]}`, borderBottom: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0 }}>{t("settings.calendar.eventTypeCategories", "Event-type categories")}</h3>
              <p style={{ ...tx.help, margin: `${T.space[1]} 0 0`, fontSize: T.text.label.fontSize }}>
                {t("settings.calendar.builtInCategoriesDriveSeedDataAndCanBeRenamedOrRecoloured", "Built-in categories drive seed data and can be renamed or recoloured. Add custom categories below — assign them to new events at create time.")}</p>
            </div>
            {dirty ? <Pill tone="warn">{t("settings.calendar.unsavedChanges", "Unsaved changes")}</Pill> : null}
          </div>

          <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
            {rows.map((row) => (
              <CategoryRow key={row.id} row={row} palette={palette} count={counts[row.id] || 0}
                onChange={(patch) => update(row.id, patch)} onRemove={() => removeRow(row.id)} />
            ))}
          </ul>

          <div style={{ padding: T.space[4], borderTop: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: T.space[3] }}>
            <span style={{ fontSize: T.text.label.fontSize, color: T.soft }}>
              {rows.length} {t("settings.calendar.categor", "categor")}{" "}{rows.length === 1 ? t('settings.calendar.text') : t('settings.calendar.text2')} · {rows.filter((r) => !r.builtIn).length} {t("settings.calendar.custom6cdfd271", "custom")}</span>
            <Btn variant="secondary" icon={<Icon.plus size={12} />} onClick={addRow}>{t("settings.calendar.addCategory", "Add category")}</Btn>
          </div>
        </Card>

        {/* Live preview */}
        <div style={{ display: "flex", flexDirection: "column", gap: T.space[4] }}>
          <Card style={{ padding: 0 }}>
            <div style={{ padding: `${T.space[3]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <h3 style={{ ...tx.h3, fontWeight: 600, margin: 0, fontSize: T.text.h3.fontSize }}>{t("settings.calendar.preview", "Preview")}</h3>
              <span style={{ fontSize: T.text.label.fontSize, color: T.soft, textTransform: "uppercase", letterSpacing: 0.04, fontWeight: 600 }}>{t("common.status.live", "Live")}</span>
            </div>
            <div style={{ padding: T.space[4] }}>
              <CalendarPreview rows={rows} />
            </div>
          </Card>

          <Card>
            <h3 style={{ ...tx.h3, fontWeight: 600, margin: `0 0 ${T.space[3]}`, fontSize: T.text.h3.fontSize }}>{t("settings.calendar.howThisWorks", "How this works")}</h3>
            <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: T.space[2], fontSize: T.text.bodySm.fontSize, color: T.n2, lineHeight: 1.55 }}>
              <li style={{ display: "flex", gap: T.space[2] }}>
                <span style={{ ...mono(), color: T.p1 }}>·</span>
                <span><b style={{ color: T.n1 }}>{t("settings.calendar.builtInCategories", "Built-in categories")}</b> {t("settings.calendar.mapSeedEventsByTheirOriginalColourRecolourThemAndEveryExisting", "map seed events by their original colour. Recolour them and every existing event repaints across week, month and day views.")}</span>
              </li>
              <li style={{ display: "flex", gap: T.space[2] }}>
                <span style={{ ...mono(), color: T.p1 }}>·</span>
                <span><b style={{ color: T.n1 }}>{t("settings.calendar.customCategories", "Custom categories")}</b> {t("settings.calendar.arePickedAtEventCreationTheyDonTMatchSeedColoursSo", "are picked at event creation. They don't match seed colours, so they start at 0 events.")}</span>
              </li>
              <li style={{ display: "flex", gap: T.space[2] }}>
                <span style={{ ...mono(), color: T.p1 }}>·</span>
                <span>{t("settings.calendar.coloursComeFromTheOnBrandTertiaryPaletteNoFreeHexPicker", "Colours come from the on-brand tertiary palette — no free hex picker keeps the calendar legible and consistent across tenants.")}</span>
              </li>
            </ul>
          </Card>
        </div>
      </div>
    </SettingsShell>
  );
}

/* ─── A single editable category row ─────────────────────────────────── */
function CategoryRow({ row, palette, count, onChange, onRemove }: {
  row: CalCategory; palette: PaletteColor[]; count: number; onChange: (p: Partial<CalCategory>) => void; onRemove: () => void;
}) {
  const t = useT();
  return (
    <li style={{ padding: `${T.space[4]} ${T.space[5]}`, borderBottom: `1px solid ${T.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: T.space[3] }}>
        <span style={{ width: 32, height: 32, borderRadius: T.radius.control, background: row.color, border: `1px solid ${alpha(T.chromeInk, 8)}`, flexShrink: 0, boxShadow: `inset 0 -2px 4px ${alpha(T.chromeInk, 8)}` }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* ui-raw: inline borderless heading-style edit field — jrni-ui Input always renders its
              own bordered/padded box (no way to suppress via props), which would visually
              redesign this into a form field instead of an inline-editable title. */}
          <input value={row.name} onChange={(e) => onChange({ name: e.target.value })}
            aria-label={t("settings.calendar.rowBuiltinBuiltInCustomCategoryName.icu", { kind: row.builtIn ? "builtIn" : "custom" })}
            style={{ width: "100%", border: "none", outline: "none", fontFamily: T.font, fontSize: T.text.h3.fontSize, fontWeight: 600, color: T.n1, padding: `${T.space[1]} 0`, background: "transparent" }} />
          <div style={{ display: "flex", alignItems: "center", gap: T.space[2], fontSize: T.text.label.fontSize, color: T.soft, marginTop: T.space[1] }}>
            {row.builtIn ? <Pill tone="neutral">{t("settings.calendar.builtIn", "Built-in")}</Pill> : <Pill tone="primary">{t("settings.calendar.custom", "Custom")}</Pill>}
            <span>·</span>
            <span>{count} {t("settings.calendar.event", "event")}{" "}{count === 1 ? "" : t('settings.calendar.text3')}{" "}{t("settings.calendar.onCalendar", "on calendar")}</span>
            {!row.builtIn ? <><span>·</span><span style={{ fontFamily: T.fontMono, fontSize: T.text.label.fontSize }}>{row.id}</span></> : null}
          </div>
        </div>
        {row.builtIn ? (
          <span title={t("settings.calendar.builtInCategoriesCanBeRenamedAndRecolouredButNotDeletedThey", "Built-in categories can be renamed and recoloured but not deleted — they're tied to seed events.")}
            style={{ fontSize: T.text.label.fontSize, color: T.soft, padding: `${T.space[1]} ${T.space[2]}`, border: `1px dashed ${T.border}`, borderRadius: T.radius.sm }}>
            {t("settings.calendar.locked", "Locked")}</span>
        ) : (
          <IconBtn label={i18nT("settings.calendar.removeRowName.icu", { value0: row.name })} onClick={onRemove}><Icon.trash size={14} /></IconBtn>
        )}
      </div>

      <div style={{ display: "flex", gap: T.space[2], marginTop: T.space[3], flexWrap: "wrap", paddingLeft: T.space[8] }}>
        {palette.map((p) => {
          const active = row.color.toUpperCase() === p.hex.toUpperCase();
          return (
            // ui-raw: bespoke round color-swatch selector (dynamic background = palette hex,
            // active-state ring) — jrni-ui IconButton imposes a fixed square chrome (border,
            // white background, box-shadow, 4px radius) that would overwrite the swatch color
            // treatment; not an icon-only action button in the adoption-map sense.
            <button key={p.hex} type="button" onClick={() => onChange({ color: p.hex })} title={p.name}
              aria-label={i18nT("settings.calendar.pNameColour.icu", { value0: p.name })} aria-pressed={active}
              style={{ width: 26, height: 26, borderRadius: T.radius.pill, background: p.hex, border: active ? `2px solid ${T.n1}` : "none", boxShadow: active ? `0 0 0 2px ${T.p1}` : `0 0 0 1px ${T.border}`, padding: 0, cursor: "pointer", flexShrink: 0 }} />
          );
        })}
      </div>
    </li>
  );
}

/* ─── Tiny live preview — week-strip showing how colours land ─────────── */
function CalendarPreview({ rows }: { rows: CalCategory[] }) {
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const slots = [
    { day: 0, top: 4, h: 30, dur: "09:00 – 10:00" },
    { day: 0, top: 38, h: 36, dur: "10:30 – 12:00" },
    { day: 1, top: 8, h: 44, dur: "09:30 – 11:30" },
    { day: 2, top: 4, h: 28, dur: "09:00 – 10:00" },
    { day: 2, top: 36, h: 50, dur: "10:30 – 13:30" },
    { day: 3, top: 12, h: 38, dur: "10:00 – 11:30" },
    { day: 4, top: 6, h: 60, dur: "09:00 – 12:30" },
  ];
  const visibleRows = rows.length > 0 ? rows : BUILT_IN_CAL_CATEGORIES;
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: T.space[1], marginBottom: T.space[2] }}>
        {days.map((d) => (
          <div key={d} style={{ fontSize: T.text.label.fontSize, fontWeight: 600, color: T.soft, textTransform: "uppercase", letterSpacing: 0.06, textAlign: "center" }}>{d}</div>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: T.space[1], position: "relative", height: 110, background: `repeating-linear-gradient(180deg, ${T.canvas} 0 27px, transparent 27px 28px)`, borderRadius: T.radius.control, border: `1px solid ${T.border}` }}>
        {days.map((_, i) => <div key={i} style={{ position: "relative", borderRight: i < 4 ? `1px solid ${T.border}` : "none" }} />)}
        {slots.map((slot, i) => {
          const cat = visibleRows[i % visibleRows.length] || { color: T.tOrange, name: "" };
          const colW = `calc((100% - 32px) / 5)`;
          return (
            <div key={i} title={i18nT("settings.calendar.catNameSlotDur.icu", { value0: cat.name, value1: slot.dur })}
              style={{ position: "absolute", left: `calc(8px + ${slot.day} * (${colW} + 4px))`, width: colW, top: slot.top, height: slot.h, background: T.white, border: `1px solid ${T.border}`, borderLeft: `3px solid ${cat.color}`, borderRadius: T.radius.sm, padding: `${T.space[1]} ${T.space[2]}`, overflow: "hidden", fontSize: T.text.label.fontSize, color: T.n1, lineHeight: 1.2, fontWeight: 600, display: "flex", flexDirection: "column", gap: T.space[1] }}>
              <span style={{ overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{cat.name}</span>
              <span style={{ color: T.soft, fontWeight: 400, fontSize: T.text.label.fontSize }}>{slot.dur}</span>
            </div>
          );
        })}
      </div>
      <div style={{ marginTop: T.space[3], display: "flex", flexWrap: "wrap", gap: T.space[2] }}>
        {visibleRows.map((c) => (
          <span key={c.id} style={{ display: "inline-flex", alignItems: "center", gap: T.space[2], fontSize: T.text.label.fontSize, color: T.n2, padding: `${T.space[1]} ${T.space[2]}`, border: `1px solid ${T.border}`, borderRadius: T.radius.pill, background: T.white }}>
            <span style={{ width: 8, height: 8, borderRadius: T.radius.pill, background: c.color }} />
            {c.name}
          </span>
        ))}
      </div>
    </div>
  );
}
